/**
 * Customer PANs — multi-PAN management.
 *
 * Endpoints:
 *   GET    /api/v1/customer/pans/             -> list this customer's PANs
 *   POST   /api/v1/customer/pans/             -> add a PAN
 *   PATCH  /api/v1/customer/pans/{id}         -> rename (set/clear the label)
 *   DELETE /api/v1/customer/pans/{id}         -> remove a PAN
 *   PATCH  /api/v1/customer/pans/{id}/primary -> set as primary
 */
import { useMutation, useQuery, type UseMutationOptions, type UseQueryOptions } from '@tanstack/react-query';
import type { ApiError } from '@finmin/api-client';
import { createKeyFactory } from '@finmin/api-client';
import { apiClient } from '@/lib/api';
import { queryClient } from './query-client';
import { profileKeys } from './profile';

import { normalizePan, type PanType } from '@finmin/utils';

// PAN type vocabulary is shared with the admin app.
export {
  PAN_REGEX,
  PAN_TYPE_LABELS,
  PAN_TYPE_COLORS,
  PAN_TYPES,
  isPanType,
  normalizePan,
  isValidPanFormat,
  derivePanType,
  panTypeLabel,
  panTypeColor,
  type PanType,
} from '@finmin/utils';

// ---------- types -------------------------------------------------------

export interface CustomerPAN {
  id: string;
  pan: string;
  pan_type: PanType;
  is_primary: boolean;
  /** Optional customer-authored name. null when never named. Never masked. */
  label: string | null;
  created_at: string;
}

/** Longest name the backend accepts. */
export const PAN_LABEL_MAX_LENGTH = 40;

/**
 * What to call this PAN in the UI — its name if it has one, else the PAN.
 * Use everywhere a PAN is labelled so a name follows the user across the app.
 */
export function panDisplayName(pan: CustomerPAN | undefined): string {
  if (!pan) return '';
  return pan.label?.trim() || pan.pan;
}

/** The customer's primary PAN — pre-selected in forms while viewing all PANs. */
export function getActivePan(pans: CustomerPAN[] | undefined): CustomerPAN | undefined {
  return pans?.find((p) => p.is_primary);
}

export function getPanById(
  pans: CustomerPAN[] | undefined,
  id: string | null | undefined,
): CustomerPAN | undefined {
  if (!id) return undefined;
  return pans?.find((p) => p.id === id);
}

// ---------- which PANs a page allows -----------------------------------------

/**
 * What the current page permits the PAN switcher to select.
 *   none    — anywhere else in the app; every PAN is selectable.
 *   service — starting a service; only PAN types the service is offered to,
 *             and "All PANs" is not a valid choice for a new request.
 *   locked  — inside an existing request, which is already filed under one PAN.
 */
export type PanConstraint =
  | { kind: 'none' }
  | { kind: 'service'; serviceLabel: string; panTypes: string[]; loading?: boolean }
  | { kind: 'locked'; panId: string; panLabel?: string };

export interface PanItemState {
  disabled: boolean;
  reason?: string;
}

export function getPanItemState(pan: CustomerPAN, constraint: PanConstraint): PanItemState {
  switch (constraint.kind) {
    case 'service': {
      if (constraint.loading) {
        return { disabled: true, reason: 'Checking which PANs this service accepts…' };
      }
      // An unknown list (service not in the catalog) imposes no restriction;
      // the backend still rejects a PAN the service does not accept.
      if (constraint.panTypes.length === 0) return { disabled: false };
      if (constraint.panTypes.includes(pan.pan_type)) return { disabled: false };
      return { disabled: true, reason: `Not available for ${constraint.serviceLabel}` };
    }
    case 'locked':
      return pan.id === constraint.panId
        ? { disabled: false }
        : { disabled: true, reason: 'This request is filed under another PAN' };
    default:
      return { disabled: false };
  }
}

/** "All PANs" is a view, not something a request or upload can be filed under. */
export function isAllSelectable(constraint: PanConstraint): PanItemState {
  switch (constraint.kind) {
    case 'service':
      return { disabled: true, reason: 'Pick a PAN to start this service' };
    case 'locked':
      return { disabled: true, reason: 'This request is filed under one PAN' };
    default:
      return { disabled: false };
  }
}

// ---------- query keys ----------------------------------------------------

export const customerPanKeys = createKeyFactory('customer.pans', {
  list: () => ['list'] as const,
});

// ---------- raw API functions -----------------------------------------------

/** GET /api/v1/customer/pans/ */
export async function listCustomerPANs(signal?: AbortSignal) {
  const { data } = await apiClient.get<CustomerPAN[]>('/api/v1/customer/pans/', { signal });
  return data;
}

/** POST /api/v1/customer/pans/ — `label` is optional and omitted when blank. */
export async function addCustomerPAN(pan: string, label?: string | null) {
  const trimmed = label?.trim();
  const { data } = await apiClient.post<CustomerPAN>('/api/v1/customer/pans/', {
    pan: normalizePan(pan),
    ...(trimmed ? { label: trimmed } : {}),
  });
  return data;
}

/**
 * PATCH /api/v1/customer/pans/{id} — set or clear this PAN's name.
 * Passing an empty string clears it; the backend normalizes blank to null.
 */
export async function renameCustomerPAN(id: string, label: string) {
  const trimmed = label.trim();
  const { data } = await apiClient.patch<CustomerPAN>(`/api/v1/customer/pans/${id}`, {
    label: trimmed || null,
  });
  return data;
}

/** DELETE /api/v1/customer/pans/{id} — 400 if the PAN has active service requests. */
export async function deleteCustomerPAN(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/customer/pans/${id}`);
}

/** PATCH /api/v1/customer/pans/{id}/primary */
export async function setPrimaryCustomerPAN(id: string): Promise<void> {
  await apiClient.patch(`/api/v1/customer/pans/${id}/primary`);
}

// ---------- hooks -----------------------------------------------------------

export function useCustomerPANs(
  options?: Omit<UseQueryOptions<CustomerPAN[], ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<CustomerPAN[], ApiError>({
    queryKey: customerPanKeys.list(),
    queryFn: ({ signal }) => listCustomerPANs(signal),
    ...options,
  });
}

/** Invalidates both this file's list and the profile (which also carries `pans[]`). */
function invalidatePans() {
  queryClient.invalidateQueries({ queryKey: customerPanKeys.list() });
  queryClient.invalidateQueries({ queryKey: profileKeys.me() });
}

/**
 * Patch one PAN into both caches from a mutation response, so an open dropdown
 * shows the change on the same frame. This is the only update on rename — see
 * useRenamePAN for why an invalidation would undo it.
 */
function writePanIntoCaches(updated: CustomerPAN) {
  const swap = (list: CustomerPAN[]) =>
    list.map((p) => (p.id === updated.id ? updated : p));

  queryClient.setQueryData<CustomerPAN[]>(customerPanKeys.list(), (old) =>
    old ? swap(old) : old,
  );
  // Typed structurally rather than as CustomerProfileResponse: profile.ts
  // already imports CustomerPAN from here, and naming the type would make the
  // import cycle.
  queryClient.setQueryData<{ pans: CustomerPAN[] }>(profileKeys.me(), (old) =>
    old?.pans ? { ...old, pans: swap(old.pans) } : old,
  );
}

export function useAddPAN(
  options?: UseMutationOptions<CustomerPAN, ApiError, { pan: string; label?: string | null }>,
) {
  const { onSuccess, ...rest } = options ?? {};
  return useMutation<CustomerPAN, ApiError, { pan: string; label?: string | null }>({
    mutationFn: ({ pan, label }) => addCustomerPAN(pan, label),
    onSuccess: (...args) => {
      invalidatePans();
      onSuccess?.(...args);
    },
    ...rest,
  });
}

/** Rename a PAN. Invalidates the profile too — the switcher reads `pans[]` from it. */
export function useRenamePAN(
  options?: UseMutationOptions<CustomerPAN, ApiError, { id: string; label: string }>,
) {
  const { onSuccess, ...rest } = options ?? {};
  return useMutation<CustomerPAN, ApiError, { id: string; label: string }>({
    mutationFn: ({ id, label }) => renameCustomerPAN(id, label),
    onSuccess: (...args) => {
      // Write the returned record straight into both caches and do NOT
      // invalidate. The profile GET that an invalidation triggers races the
      // backend's post-response commit and comes back with the old name,
      // clobbering the value the user just saved. The PATCH response is
      // authoritative, so seeding alone keeps every reader correct.
      writePanIntoCaches(args[0]);
      onSuccess?.(...args);
    },
    ...rest,
  });
}

export function useDeletePAN(
  options?: UseMutationOptions<void, ApiError, { id: string }>,
) {
  const { onSuccess, ...rest } = options ?? {};
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => deleteCustomerPAN(id),
    onSuccess: (...args) => {
      invalidatePans();
      onSuccess?.(...args);
    },
    ...rest,
  });
}

export function useSetPrimaryPAN(
  options?: UseMutationOptions<void, ApiError, { id: string }>,
) {
  const { onSuccess, ...rest } = options ?? {};
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => setPrimaryCustomerPAN(id),
    onSuccess: (...args) => {
      invalidatePans();
      onSuccess?.(...args);
    },
    ...rest,
  });
}
